import { StorageSerializers, useLocalStorage } from "@vueuse/core";
import { computed, ComputedRef, onMounted, onUnmounted, Ref, ref } from "vue";
import type { Router } from "vue-router";
import type { UserRolesResponse } from "../../lib/dynamodb/types";

export interface CredentialsStorage {
	error: boolean;
	creds: UserRolesResponse;
	timestamp: number;
	isLoading: boolean;
}

export interface AuthState {
	credentials: Ref<CredentialsStorage | null>;
	login: (params: { username: string; password: string } | { jwt: string; build_policies_hash: string }) => Promise<void>;
	logout: () => Promise<void>;
	isLoggedIn: ComputedRef<boolean>;
	isLoading: Ref<boolean>;
}

declare global {
	interface Window {
		VERSION: "user" | "admin";
	}
}

const MAX_REFRESH_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 5000;
const REFRESH_BUFFER_MS = 60000; // Refresh 1 minute before expiry

/**
 * Composable to manage authentication state and AWS credentials
 */
export function useAuth(
	router: Router,
	loginEndpoint: string = "https://api.orangead.ca/user/login",
	_logoutEndpoint: string = "https://api.orangead.ca/user/logout"
): AuthState {
	const credentials = useLocalStorage<CredentialsStorage | null>("credentials" + window.VERSION, null, {
		serializer: StorageSerializers.object,
		deep: true,
	});

	const isLoading = ref(true);
	const isLoggedIn = computed(() => !isLoading.value && !!credentials.value);

	let refreshTimeoutId: ReturnType<typeof setTimeout> | null = null;

	const clearRefreshTimeout = () => {
		if (refreshTimeoutId !== null) {
			clearTimeout(refreshTimeoutId);
			refreshTimeoutId = null;
		}
	};

	const scheduleRefresh = () => {
		clearRefreshTimeout();

		const expiresAt = credentials.value?.creds?.expires_at;
		if (!expiresAt) {
			return;
		}

		const expiresAtMs = expiresAt * 1000;
		const msUntilExpiry = expiresAtMs - Date.now();
		const refreshDelay = Math.max(msUntilExpiry - REFRESH_BUFFER_MS, 0);

		if (refreshDelay > 0) {
			refreshTimeoutId = setTimeout(() => refreshWithRetry(), refreshDelay);
		} else {
			// Already within the buffer window, refresh immediately
			refreshWithRetry();
		}
	};

	const refreshWithRetry = async (attempt = 0): Promise<void> => {
		const jwt = credentials.value?.creds?.jwt;
		const buildPoliciesHash = credentials.value?.creds?.build_policies_hash;

		if (!jwt || !buildPoliciesHash) {
			credentials.value = null;
			await router.push("/login");
			return;
		}

		try {
			await login({ jwt, build_policies_hash: buildPoliciesHash });
		} catch (error) {
			if (attempt < MAX_REFRESH_RETRIES - 1) {
				const delay = RETRY_BASE_DELAY_MS * Math.pow(2, attempt);
				refreshTimeoutId = setTimeout(() => refreshWithRetry(attempt + 1), delay);
			} else {
				credentials.value = null;
				await router.push("/login");
			}
		}
	};

	const login = async (
		params: { username: string; password: string } | { jwt: string; build_policies_hash: string }
	): Promise<void> => {
		try {
			const body = JSON.stringify(params);

			const response = await fetch(loginEndpoint, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body,
			});

			if (!response.ok) {
				credentials.value = null;
				if (response.status === 401) {
					await router.push("/login");
					throw new Error("Invalid username or password");
				}
				if (response.status === 403) {
					await router.push("/login");
					throw new Error("Access denied");
				}
				await router.push("/login");
				throw new Error(`Login failed: ${response.statusText}`);
			}

			if (response.status === 200) {
				credentials.value = {
					error: false,
					creds: (await response.json()) as UserRolesResponse,
					timestamp: Date.now(),
					isLoading: false,
				};
				isLoading.value = false;
				scheduleRefresh();
			}

			// Redirect to projects page only on user-initiated login
			if ("username" in params) {
				window.scrollTo({ top: 0, behavior: "smooth" });
				await router.push("/projects");
			}
		} catch (error) {
			if (error instanceof Error) {
				throw error;
			}
			throw new Error("Unable to connect to server");
		}
	};

	onMounted(async () => {
		const expiresAt = credentials.value?.creds?.expires_at;

		if (credentials.value && expiresAt) {
			const expiresAtMs = expiresAt * 1000;

			if (expiresAtMs < Date.now()) {
				// Token expired — attempt refresh
				isLoading.value = true;
				await refreshWithRetry();
			} else {
				// Token still valid — schedule refresh
				isLoading.value = false;
				scheduleRefresh();
			}
		} else {
			// No credentials at all
			isLoading.value = false;
			await router.push("/login");
		}
	});

	onUnmounted(() => {
		clearRefreshTimeout();
	});

	const logout = async (): Promise<void> => {
		clearRefreshTimeout();
		credentials.value = null;
		window.scrollTo({ top: 0, behavior: "smooth" });
		await router.push("/login");
	};

	return {
		isLoading,
		isLoggedIn,
		credentials,
		login,
		logout,
	};
}